import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, Linkedin, Search, Users, Eye } from 'lucide-react';

export default function LinkedInOptimizationPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.linkedIn} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Professional Profile</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              LinkedIn Profile Optimization Service
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Make your LinkedIn profile work harder for you. Professional headline, keyword-rich About section, and optimization tips to improve recruiter visibility.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* What's Included */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
            What's Included in LinkedIn Optimization
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Linkedin, title: 'Professional Headline', desc: 'A compelling, keyword-rich headline that captures what you do and who you help.' },
              { icon: Search, title: 'Keyword-Rich About Section', desc: 'An engaging About section optimized for LinkedIn search and recruiter queries.' },
              { icon: Eye, title: 'Profile Optimization Tips', desc: 'Recommendations for improving your entire profile: photo, banner, experience, skills.' },
              { icon: Users, title: 'Recruiter Visibility', desc: 'Strategic keyword placement to help recruiters find you in LinkedIn searches.' },
              { icon: Check, title: 'Experience Alignment', desc: 'Ensure your LinkedIn aligns with your resume for consistent positioning.' },
              { icon: Check, title: 'Revision Support', desc: 'Reasonable revisions included within the agreed scope.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Why LinkedIn Matters */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Why LinkedIn Optimization Matters
            </h2>
            
            <div className="space-y-4">
              {[
                '87% of recruiters use LinkedIn to evaluate candidates during the hiring process',
                'A strong headline and About section can significantly increase profile views',
                'Keyword optimization helps you appear in recruiter searches for your target roles',
                'Consistent messaging between resume and LinkedIn reinforces your professional brand',
                'Many employers check LinkedIn even after receiving your resume',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                  <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bundle Offer */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <Card className="p-8 rounded-3xl bg-teal-50 border-2 border-teal-200">
            <div className="text-center">
              <Badge className="bg-teal-600 text-white mb-4">Popular Combination</Badge>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
                Resume + LinkedIn Bundle
              </h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Many clients pair LinkedIn optimization with a resume rewrite for consistent positioning across both platforms. Mention it in your intake and we'll include it in your scope.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite</Link>
                <span className="text-slate-300">+</span>
                <span className="font-semibold text-slate-900">LinkedIn Optimization</span>
              </div>
            </div>
          </Card>
        </section>

        {/* Related Services */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/cover-letter" className="text-teal-700 hover:text-teal-800 underline">Cover Letter Support</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View All Pricing</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to optimize your LinkedIn profile?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and let us know you need LinkedIn help. We'll review and confirm scope before invoicing.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
